import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Lock, Phone, Mail, CheckCircle2, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ApplicationDocuments from "@/components/applications/ApplicationDocuments";
import MessageThread from "@/components/messages/MessageThread";
import api, { API, formatApiErrorDetail } from "@/lib/api";

export default function ApplicationsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(searchParams.get("offer_id") || "");
  const [applicants, setApplicants] = useState(null);
  const [unlockingId, setUnlockingId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [expandedMessagesId, setExpandedMessagesId] = useState(null);

  useEffect(() => {
    api.get("/offers/mine").then(({ data }) => {
      setOffers(data);
      if (!selectedOffer && data.length > 0) setSelectedOffer(data[0].id);
    });
  }, []);

  const loadApplicants = () => {
    if (!selectedOffer) return;
    api.get(`/applications/producer/offer/${selectedOffer}`).then(({ data }) => setApplicants(data));
  };

  useEffect(() => { loadApplicants(); }, [selectedOffer]);

  const handleUnlock = async (applicationId) => {
    setUnlockingId(applicationId);
    try {
      await api.post(`/applications/${applicationId}/unlock`);
      toast.success(t("application.contactRevealed"));
      loadApplicants();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(t("credits.noCreditsMessage"));
        navigate("/dashboard/producer/credits");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
      }
    } finally {
      setUnlockingId(null);
    }
  };

  const handleCloseMission = async (applicationId) => {
    if (!window.confirm(t("application.confirmCloseMission"))) return;
    setClosingId(applicationId);
    try {
      await api.post(`/applications/${applicationId}/close`);
      toast.success(t("common.success"));
      loadApplicants();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setClosingId(null);
    }
  };

  const setApplicantDocuments = (applicationId, sharedDocuments) => {
    setApplicants((prev) => prev.map((a) => (a.id === applicationId ? { ...a, shared_documents: sharedDocuments } : a)));
  };

  return (
    <div data-testid="producer-applications-tab">
      <div className="mb-6 max-w-xs">
        <Select value={selectedOffer} onValueChange={(v) => { setSelectedOffer(v); setSearchParams({ offer_id: v }); }}>
          <SelectTrigger className="h-12 rounded-xl bg-white border-black/10" data-testid="producer-applications-offer-select">
            <SelectValue placeholder={t("dashboard.tabOffers")} />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {offers.map((o) => (
              <SelectItem key={o.id} value={o.id} data-testid={`producer-applications-offer-option-${o.id}`}>
                {t(`lookups.missionTypes.${o.mission_type}`)} &middot; {t(`lookups.regions.${o.region}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!applicants ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7]" /></div>
      ) : applicants.length === 0 ? (
        <p className="text-center py-16 text-[#2B2B2B]/50" data-testid="producer-applications-empty-message">{t("search.noResults")}</p>
      ) : (
        <div className="space-y-4">
          {applicants.map((app) => (
            <div key={app.id} className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5" data-testid={`producer-applicant-row-${app.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-[#FAFAFA] border border-black/10 overflow-hidden flex items-center justify-center shrink-0">
                    {app.candidate?.photo_path ? (
                      <img src={`${API}/profiles/files/${app.candidate.photo_path}`} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="font-bold text-[#4ba3b7]">{app.candidate ? app.candidate?.first_name?.[0] : "?"}</span>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold" data-testid={`producer-applicant-name-${app.id}`}>
                      {app.candidate ? `${app.candidate.first_name || ""} ${app.candidate.last_name || ""}` : t("admin.deletedAccountLabel")}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="rounded-full text-xs">{t(`application.status${app.status.charAt(0).toUpperCase() + app.status.slice(1)}`)}</Badge>
                      {app.mission_closed && (
                        <Badge className="rounded-full text-xs bg-[#34C759]/10 text-[#34C759] border-0 gap-1" data-testid={`producer-applicant-closed-badge-${app.id}`}>
                          <CheckCircle2 className="h-3 w-3" /> {t("application.missionClosedOn", { date: new Date(app.closed_at).toLocaleDateString() })}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {app.status === "unlocked" ? (
                    <div className="flex flex-col text-sm gap-1">
                      {app.candidate?.phone && <span className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#34C759]" /> {app.candidate.phone}</span>}
                      {app.candidate?.email && <span className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#34C759]" /> {app.candidate.email}</span>}
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => handleUnlock(app.id)} disabled={unlockingId === app.id}
                            className="rounded-lg bg-[#4ba3b7] hover:bg-[#3D8A9A] gap-2" data-testid={`producer-unlock-applicant-button-${app.id}`}>
                      <Lock className="h-3.5 w-3.5" /> {unlockingId === app.id ? t("common.loading") : `${t("application.unlockCandidate")} (${t("credits.unlockCost")})`}
                    </Button>
                  )}
                  {app.status === "unlocked" && !app.mission_closed && (
                    <Button size="sm" variant="outline" onClick={() => handleCloseMission(app.id)} disabled={closingId === app.id}
                            className="rounded-lg gap-2" data-testid={`producer-close-mission-button-${app.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> {closingId === app.id ? t("common.loading") : t("application.closeMission")}
                    </Button>
                  )}
                </div>
              </div>

              {app.status === "unlocked" && (
                <div className="mt-4 pt-4 border-t border-black/5">
                  <ApplicationDocuments applicationId={app.id} documents={app.shared_documents} showSuggestions
                                        onUploaded={(docs) => setApplicantDocuments(app.id, docs)} />
                  <button onClick={() => setExpandedMessagesId(expandedMessagesId === app.id ? null : app.id)}
                          className="flex items-center gap-1.5 text-sm font-semibold text-[#4ba3b7] hover:underline mt-4"
                          data-testid={`producer-toggle-messages-button-${app.id}`}>
                    <MessageCircle className="h-4 w-4" /> {t("messages.messagesTitle")}
                    {expandedMessagesId === app.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                  {expandedMessagesId === app.id && (
                    <div className="mt-3">
                      <MessageThread applicationId={app.id} compact />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
